import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Eye, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { DocumentCommentsPanelTarget } from "../../comments/documentCommentsPanelContext";
import { DocumentCommentsThread } from "../comments/DocumentCommentsThread";

interface DocumentCommentsPanelProps {
  target: DocumentCommentsPanelTarget | null;
  onClose: () => void;
  /** Open the target document in the viewer, optionally on a page. */
  onOpenDocument: (documentId: number, page: number | null) => void;
}

/**
 * The comments panel a Documents view slides in over the list — the same
 * right-hand shell as `SignaturePanel`, holding `DocumentCommentsThread` for
 * one document (and its version lineage). Opened from the toolbar, the row
 * kebab and the right-click menu; the viewer's rail is the other mount.
 */
export function DocumentCommentsPanel({
  target,
  onClose,
  onOpenDocument,
}: DocumentCommentsPanelProps) {
  const { t } = useLanguage();
  const c = t.documents.comments;
  return (
    <DialogPrimitive.Root
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-lg flex-col border-l bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {target && (
            <>
              <header className="flex shrink-0 items-start justify-between gap-3 border-b px-4 py-3">
                <div className="min-w-0">
                  <DialogPrimitive.Title className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {c.title}
                  </DialogPrimitive.Title>
                  <p className="truncate text-sm font-medium" title={target.filename}>
                    {target.filename}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => onOpenDocument(target.documentId, null)}
                  >
                    <Eye className="mr-1 h-3.5 w-3.5" />
                    {c.openDocument}
                  </Button>
                  <DialogPrimitive.Close asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={t.common.close}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </DialogPrimitive.Close>
                </div>
              </header>
              <DocumentCommentsThread
                key={target.documentId}
                documentId={target.documentId}
                buildingId={target.buildingId}
                pagedRenderer={false}
                crossVersionNavigation
                onGoToPage={(page, documentId) => onOpenDocument(documentId, page)}
                initialThreadId={target.initialThreadId ?? null}
                className="min-h-0 flex-1"
              />
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
